/**
 * Centralized tenant timezone utilities.
 * All date display should use tenant timezone, never browser timezone.
 * The tenant timezone comes from TenantDataContext.companyTimezone (IANA format like "America/New_York").
 */

// Windows to IANA timezone mappings.
// NOTE: There is a copy of this mapping in SlotVisualizationManager.ts — this is the canonical version.
//
// This must stay a superset of every Windows timezone id the backend can persist
// on CompanySettings.TimeZone. A short table used to silently fall back to 'UTC'
// for anything unmapped, which rendered raw UTC wall-clock times on the Calendar
// (e.g. a 10:30 AM Eastern appointment shown as 2:30 PM). The list below covers
// the full US/Canada set plus the common international zones from the CLDR
// windowsZones mapping.
const WINDOWS_TO_IANA: Record<string, string> = {
  // United States
  'Eastern Standard Time': 'America/New_York',
  'US Eastern Standard Time': 'America/Indiana/Indianapolis',
  'Central Standard Time': 'America/Chicago',
  'Central Standard Time (Mexico)': 'America/Mexico_City',
  'Mountain Standard Time': 'America/Denver',
  'Mountain Standard Time (Mexico)': 'America/Chihuahua',
  'US Mountain Standard Time': 'America/Phoenix',
  'Pacific Standard Time': 'America/Los_Angeles',
  'Pacific Standard Time (Mexico)': 'America/Tijuana',
  'Alaskan Standard Time': 'America/Anchorage',
  'Aleutian Standard Time': 'America/Adak',
  'Hawaiian Standard Time': 'Pacific/Honolulu',
  // Canada
  'Atlantic Standard Time': 'America/Halifax',
  'Newfoundland Standard Time': 'America/St_Johns',
  'Central America Standard Time': 'America/Guatemala',
  'Canada Central Standard Time': 'America/Regina',
  'SA Pacific Standard Time': 'America/Bogota',
  'Yukon Standard Time': 'America/Whitehorse',
  // Other Americas
  'Greenland Standard Time': 'America/Godthab',
  'SA Eastern Standard Time': 'America/Cayenne',
  'Argentina Standard Time': 'America/Buenos_Aires',
  'E. South America Standard Time': 'America/Sao_Paulo',
  'Pacific SA Standard Time': 'America/Santiago',
  // Europe / Africa
  'GMT Standard Time': 'Europe/London',
  'Greenwich Standard Time': 'Atlantic/Reykjavik',
  'W. Europe Standard Time': 'Europe/Berlin',
  'Central Europe Standard Time': 'Europe/Budapest',
  'Central European Standard Time': 'Europe/Warsaw',
  'Romance Standard Time': 'Europe/Paris',
  'GTB Standard Time': 'Europe/Bucharest',
  'E. Europe Standard Time': 'Europe/Chisinau',
  'FLE Standard Time': 'Europe/Kiev',
  'Russian Standard Time': 'Europe/Moscow',
  'South Africa Standard Time': 'Africa/Johannesburg',
  // Asia / Pacific
  'India Standard Time': 'Asia/Kolkata',
  'China Standard Time': 'Asia/Shanghai',
  'Tokyo Standard Time': 'Asia/Tokyo',
  'Korea Standard Time': 'Asia/Seoul',
  'Singapore Standard Time': 'Asia/Singapore',
  'W. Australia Standard Time': 'Australia/Perth',
  'AUS Eastern Standard Time': 'Australia/Sydney',
  'New Zealand Standard Time': 'Pacific/Auckland',
  // UTC family
  'UTC': 'UTC',
  'Coordinated Universal Time': 'UTC',
};

// Track unmapped ids we have already warned about so a repeated render does not
// spam the console with the same message.
const warnedUnmappedTimezones = new Set<string>();

/**
 * Converts a Windows timezone ID to IANA format for use with Intl APIs.
 * If already IANA (contains '/'), returns as-is.
 * Falls back to 'UTC' for unknown/missing values — never browser timezone — but
 * warns once per unmapped id so the silent-degrade-to-UTC failure mode (which
 * showed raw UTC times on the Calendar) is observable instead of invisible.
 */
export function toIanaTimezone(timezoneId: string | null | undefined): string {
  if (!timezoneId) return 'UTC';

  // Already IANA format (e.g. "America/New_York")
  if (timezoneId.includes('/')) return timezoneId;

  const mapped = WINDOWS_TO_IANA[timezoneId];
  if (mapped) return mapped;

  if (!warnedUnmappedTimezones.has(timezoneId)) {
    warnedUnmappedTimezones.add(timezoneId);
    // eslint-disable-next-line no-console
    console.warn(
      `[tenantTime] Unmapped Windows timezone id "${timezoneId}" — falling back to UTC. ` +
        'Add it to WINDOWS_TO_IANA in src/utils/tenantTime.ts to render tenant-local times correctly.'
    );
  }
  return 'UTC';
}

/**
 * Parse a date string from the backend, treating bare ISO timestamps (no 'Z'/offset) as UTC.
 * The .NET backend sends UTC timestamps without a 'Z' suffix — appending it forces correct UTC
 * interpretation instead of browser-local interpretation.
 */
function parseBackendDate(dateStr: string | Date): Date {
  if (dateStr instanceof Date) return dateStr;

  // If the string contains 'T' but has no timezone indicator, append 'Z' to treat as UTC.
  if (
    dateStr.includes('T') &&
    !dateStr.endsWith('Z') &&
    !dateStr.includes('+') &&
    !dateStr.includes('-', dateStr.indexOf('T'))
  ) {
    return new Date(dateStr + 'Z');
  }

  // Date-only strings (YYYY-MM-DD) — treat as UTC midnight to avoid day-shift.
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'T00:00:00Z');
  }

  return new Date(dateStr);
}

/**
 * Get the current date in tenant timezone as a YYYY-MM-DD string.
 * Use this instead of new Date() when you need "today" in tenant context.
 */
export function getTenantToday(tenantTimezone: string | null | undefined): string {
  const ianaTimezone = toIanaTimezone(tenantTimezone);
  const now = new Date();

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);

  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';

  return `${year}-${month}-${day}`;
}

/**
 * Get the YYYY-MM-DD date key of an instant as seen in the tenant timezone.
 * Use when bucketing UTC timestamps (e.g. appointment start times) by tenant
 * calendar day — the browser's local date can differ by a day near midnight.
 */
export function dateKeyInTenantTz(date: Date, tenantTimezone: string | null | undefined): string {
  const ianaTimezone = toIanaTimezone(tenantTimezone);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${year}-${month}-${day}`;
}

/**
 * Format a date/datetime string in the tenant timezone.
 * Handles both ISO datetime strings and date-only strings.
 * Always uses tenant timezone; falls back to UTC, never browser timezone.
 */
export function formatDateInTenantTz(
  dateStr: string | Date | null | undefined,
  tenantTimezone: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateStr) return '—';

  try {
    const date = parseBackendDate(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);

    const ianaTimezone = toIanaTimezone(tenantTimezone);
    const formatOptions: Intl.DateTimeFormatOptions = options ?? {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };

    return new Intl.DateTimeFormat('en-US', {
      ...formatOptions,
      timeZone: ianaTimezone,
    }).format(date);
  } catch {
    return String(dateStr);
  }
}

/**
 * Format just the time portion in tenant timezone.
 * Returns "2:30 PM" style format.
 */
export function formatTimeInTenantTz(
  dateStr: string | Date | null | undefined,
  tenantTimezone: string | null | undefined
): string {
  if (!dateStr) return '—';

  try {
    const date = parseBackendDate(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);

    const ianaTimezone = toIanaTimezone(tenantTimezone);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: ianaTimezone,
    }).format(date);
  } catch {
    return String(dateStr);
  }
}

/**
 * Format date and time together in tenant timezone.
 * Returns "Jan 15, 2026, 2:30 PM" style format.
 */
export function formatDateTimeInTenantTz(
  dateStr: string | Date | null | undefined,
  tenantTimezone: string | null | undefined
): string {
  if (!dateStr) return '—';

  try {
    const date = parseBackendDate(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);

    const ianaTimezone = toIanaTimezone(tenantTimezone);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: ianaTimezone,
    }).format(date);
  } catch {
    return String(dateStr);
  }
}

/**
 * Convert a date string (YYYY-MM-DD) and time string (HH:mm) in tenant local timezone
 * to a UTC Date object. Use this when slot times from the backend (which are in tenant
 * local time) need to be sent back as UTC for API calls.
 *
 * Uses an iterative correction: start with the wall-clock numbers as UTC, check what
 * that instant looks like in the target timezone, then adjust by the offset. Two passes
 * handle DST edge cases reliably.
 */
export function tenantLocalToUtcDate(
  dateStr: string,
  timeStr: string,
  tenantTimezone: string | null | undefined
): Date {
  const ianaTimezone = toIanaTimezone(tenantTimezone);
  // Parse by prefix, not split: backend DateOnly fields often serialize as
  // "YYYY-MM-DDT00:00:00" and times as "HH:mm:ss". A naive split produced
  // NaN parts -> Invalid Date -> Intl.formatToParts threw "Invalid time
  // value" (which is how Routy's slot parsing used to die).
  const dm = String(dateStr ?? '').match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  const tm = String(timeStr ?? '').match(/^(\d{1,2}):(\d{1,2})/);
  if (!dm || !tm) return new Date(NaN);
  const year = Number(dm[1]);
  const month = Number(dm[2]);
  const day = Number(dm[3]);
  const hour = Number(tm[1]);
  const minute = Number(tm[2]);

  let utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);

  for (let i = 0; i < 2; i++) {
    const tzParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: ianaTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date(utcGuess));

    const tzH = Number(tzParts.find((p) => p.type === 'hour')?.value ?? 0);
    const tzM = Number(tzParts.find((p) => p.type === 'minute')?.value ?? 0);
    let diff = (tzH === 24 ? 0 : tzH) * 60 + tzM - (hour * 60 + minute);
    // Normalize to [-720, 720] so midnight-crossing (e.g. UTC-5 at 1 AM local) doesn't
    // produce a 19-hour delta in the wrong direction instead of a 5-hour delta.
    if (diff > 12 * 60) diff -= 24 * 60;
    if (diff < -12 * 60) diff += 24 * 60;

    if (diff === 0) break;
    utcGuess -= diff * 60_000;
  }

  return new Date(utcGuess);
}

/**
 * Returns a Date object in the browser-local frame whose wall-clock numbers
 * (year/month/day/hour/minute/second) match what the wall clock currently
 * reads in the tenant timezone.
 *
 * Use when comparing to "shifted" Date objects parsed from the backend's
 * tenant-local time strings (e.g. `meetingStartTime`, `startTime`). Those
 * Dates have wall-clock numbers matching tenant intent but their underlying
 * UTC instant is offset by (browserOffset - tenantOffset). Comparing them
 * to `Date.now()` directly produces wrong results when the impersonated
 * tenant lives in a different timezone than the browser (the most common
 * case is a Routemize admin in EDT impersonating a customer in PT).
 */
export function tenantShiftedNow(tenantTimezone: string | null | undefined): Date {
  const ianaTimezone = toIanaTimezone(tenantTimezone);
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const hour = get('hour');
  // `Intl` can return "24" for midnight in some browsers; normalize to 0.
  const hh = hour === 24 ? 0 : hour;
  return new Date(get('year'), get('month') - 1, get('day'), hh, get('minute'), get('second'), 0);
}

/**
 * Convert a "shifted" Date — whose wall-clock numbers represent tenant local
 * time — into the real UTC Date for that tenant wall-clock moment.
 *
 * Use when sending a Date built or manipulated in the browser-local frame
 * (e.g. from a UI time picker on the timeline) to an API that expects the
 * actual UTC moment matching the tenant's wall clock. Without this, the
 * stop is created at the admin's UTC interpretation of the wall clock and
 * lands in the wrong slot for the tenant (off by browser/tenant offset).
 */
export function tenantWallClockToUtc(
  d: Date,
  tenantTimezone: string | null | undefined
): Date {
  const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return tenantLocalToUtcDate(dateStr, timeStr, tenantTimezone);
}

/**
 * Build a real UTC Date for a wall-clock time ("HH:MM") on the calendar day
 * of `selectedDate`, anchored to the tenant timezone.
 *
 * Use this when constructing synthetic Appointment Dates (workday start/end,
 * lunch break fallbacks, etc.) so they live in the same UTC frame as real
 * appointment Dates parsed from the backend. Formatting any of those Dates
 * via `formatTime(d, tenantTzIana)` then yields the tenant's wall clock
 * regardless of where the admin's browser is.
 *
 * The calendar day is taken from `selectedDate.getFullYear/getMonth/getDate`,
 * which represents whatever day the admin picked in their own frame.
 */
export function tenantLocalDateAt(
  selectedDate: Date,
  timeStr: string,
  tenantTimezone: string | null | undefined
): Date {
  const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
  return tenantLocalToUtcDate(dateStr, timeStr, tenantTimezone);
}

/**
 * Format just the date portion in tenant timezone.
 * Returns "Jan 15, 2026" style format.
 */
export function formatDateOnlyInTenantTz(
  dateStr: string | Date | null | undefined,
  tenantTimezone: string | null | undefined
): string {
  if (!dateStr) return '—';

  try {
    const date = parseBackendDate(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);

    const ianaTimezone = toIanaTimezone(tenantTimezone);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: ianaTimezone,
    }).format(date);
  } catch {
    return String(dateStr);
  }
}
